import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Modal, Input, Select } from "antd";
import { useNavigate } from "react-router-dom";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
// 自家组件
import MDTypography from "components/MDTypography";
import DataTable from "pages/Tables/DataTable";
import { useToast } from "components/Toast";

// 导入接口
import { getApproval, approveTransferApply } from "api";

const { Option } = Select;
const { TextArea } = Input;

const Dashboard = () => {
  const toast = useToast();
  const navigate = useNavigate();

  // 状态管理（外部搜索相关状态）
  const [menu, setMenu] = useState(null);
  const [reviewData, setReviewData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);
  const [searchTerm, setSearchTerm] = useState(""); // 外部搜索关键词
  const [selectedStatus, setSelectedStatus] = useState("all"); // 外部状态筛选

  // 菜单控制
  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = () => setMenu(null);

  // 加载审核列表数据（使用外部搜索和状态筛选参数）
  const fetchApprovalData = useCallback(async () => {
    try {
      setLoading(true);
      // 仅传递外部搜索和状态筛选参数
      const response = await getApproval({
        page: currentPage + 1, // 接口页码从1开始
        page_size: pageSize,
        keyword: searchTerm || undefined, // 外部搜索关键词
        status: selectedStatus === "all" ? undefined : selectedStatus, // 外部状态筛选
      });

      if (response.status === 0) {
        setReviewData(response.result.result || []);
        setTotal(response.result.total);
      } else {
        toast(`数据加载失败: ${response.msg || "未知错误"}`, "error");
        setReviewData([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("获取审核列表失败:", error);
      toast("网络异常，无法加载数据", "error");
      setReviewData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedStatus, currentPage, pageSize, toast]);

  // 组件挂载时加载数据 + 筛选条件变化时重新加载
  useEffect(() => {
    fetchApprovalData();
  }, [fetchApprovalData]);

  // 分页切换回调
  const handlePageChange = (newPageIndex) => {
    setCurrentPage(newPageIndex);
  };

  // 每页条数变更
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0); // 切换条数后重置到第一页
  };

  // 外部搜索触发（点击搜索按钮）
  const handleSearch = () => {
    setCurrentPage(0); // 搜索后重置到第一页
    fetchApprovalData();
  };

  // 重置筛选条件
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedStatus("all");
    setCurrentPage(0);
    fetchApprovalData();
  };

  // 审核通过操作
  const handleApprove = useCallback(
    (item) => {
      Modal.confirm({
        title: "确认审核通过",
        content: `是否通过「${item.apply_no}」的申请？`,
        okText: "确认",
        cancelText: "取消",
        onOk: async () => {
          try {
            setLoading(true);
            const response = await approveTransferApply({ 
              apply_no: item.apply_no,
              action: "APPROVE"
            });
            if (response.status !== 0) {
              throw new Error(response.msg || "审核失败");
            }

            toast("审核通过成功", "success");
            fetchApprovalData(); // 重新加载当前页数据
          } catch (error) {
            toast(`操作失败: ${error.message}`, "error");
            setLoading(false);
          }
        },
      });
    },
    [fetchApprovalData, toast]
  );

  // 审核拒绝操作
  const handleReject = useCallback(
    (item) => {
      let rejectReason = "";
      Modal.confirm({
        title: "确认审核拒绝",
        content: (
          <div>
            <p>是否拒绝「{item.apply_no}」的申请？</p>
            <TextArea
              rows={3}
              placeholder="请输入拒绝原因（必填）"
              onChange={(e) => (rejectReason = e.target.value.trim())}
              required
            />
          </div>
        ),
        okText: "确认",
        cancelText: "取消",
        onOk: async () => {
          if (!rejectReason) {
            toast("请输入拒绝原因", "warning");
            return;
          }

          try {
            setLoading(true);
            const response = await approveTransferApply({ 
              apply_no: item.apply_no,
              action: "REJECT",
              remark: rejectReason
            });
            if (response.status !== 0) {
              throw new Error(response.msg || "拒绝失败");
            }

            toast("审核拒绝成功", "success");
            fetchApprovalData(); // 重新加载当前页数据
          } catch (error) {
            toast(`操作失败: ${error.message}`, "error");
            setLoading(false);
          }
        },
      });
    },
    [fetchApprovalData, toast]
  );

  // 申请人信息组件
  const Applicant = ({ payeeInfo }) => (
    <div display="flex" alignItems="center" lineHeight={1}>
      <div ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="regular">
          {payeeInfo.name}
        </MDTypography>
        <MDTypography variant="caption" color="textSecondary">
          {payeeInfo.identity_type === "ALIPAY_LOGON_ID" ? "支付宝" : "未知账号"}
        </MDTypography>
      </div>
    </div>
  );

  // 内容省略显示组件
  const Content = ({ text, maxWidth = 250 }) => (
    <MDTypography
      display="block"
      color="text"
      sx={{
        fontSize: 14,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
        maxWidth: `${maxWidth}px`,
      }}
    >
      {text || "无内容"}
    </MDTypography>
  );

  // 金额格式化组件
  const AmountDisplay = ({ amount }) => (
    <MDTypography variant="body1" color="primary" fontWeight="medium">
      ¥{Number(amount).toFixed(2)}
    </MDTypography>
  );

  // 状态标签组件
  const StatusBadge = ({ status }) => {
    switch (status) {
      case "PENDING":
        return (
          <MDBadge
            badgeContent="待审核"
            color="warning"
            size="sm"
          />
        );
      case "APPROVED":
        return (
          <MDBadge
            badgeContent="已通过"
            color="success"
            size="sm"
          />
        );
      case "REJECTED":
        return (
          <MDBadge
            badgeContent="已拒绝"
            color="error"
            size="sm"
          />
        );
      default:
        return <MDBadge badgeContent={status} color="dark" size="sm" />;
    }
  };

  // 表格配置（不包含任何搜索相关逻辑）
  const { columns, rows } = useMemo(
    () => ({
      columns: [
        { Header: "申请单号", accessor: "applyNo", width: "18%", align: "left" },
        { Header: "申请金额", accessor: "amount", width: "12%", align: "left" },
        { Header: "申请人", accessor: "applicant", width: "20%", align: "left" },
        { Header: "申请账号", accessor: "account", width: "18%", align: "left" },
        { Header: "申请内容", accessor: "content", width: "15%", align: "left" },
        { Header: "状态", accessor: "status", width: "10%", align: "center" },
        { Header: "操作", accessor: "action", align: "center" },
      ],
      rows: reviewData.map((item) => ({
        applyNo: item.apply_no,
        amount: <AmountDisplay amount={item.trans_amount} />,
        applicant: <Applicant payeeInfo={item.payee_info} />,
        account: <Content text={item.applicant_name} maxWidth={150} />,
        content: <Content text={item.remark} maxWidth={150} />,
        status: (
          <div ml={-1}>
            <StatusBadge status={item.apply_status} />
          </div>
        ),
        action: (
          <MDTypography component="div" variant="caption" color="text">
            {item.apply_status === "PENDING" && (
              <>
                <MDButton
                  color="success"
                  size="small"
                  sx={{ marginLeft: "10px" }}
                  onClick={() => handleApprove(item)}
                  disabled={loading}
                >
                  通过
                </MDButton>
                <MDButton
                  color="error"
                  size="small"
                  sx={{ marginLeft: "10px" }}
                  onClick={() => handleReject(item)}
                  disabled={loading}
                >
                  拒绝
                </MDButton>
              </>
            )}
          </MDTypography>
        ),
      })),
    }),
    [reviewData, loading, handleApprove, handleReject]
  );


  // 待审核数量统计
  const pendingCount = reviewData.filter(
    (item) => item.apply_status === "PENDING"
  ).length;

  return (
    <DashboardLayout>
      <DashboardNavbar />

<div style={{
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "16px 24px", // 对应原p={3}
  borderBottom: "1px solid #e0e0e0",
  marginBottom: "16px"
}}>
  <div>
  
    <div style={{
      display: "flex",
      alignItems: "center",
      marginTop: "8px"
    }}>
      <Icon
        sx={{
          fontWeight: "bold",
          color: ({ palette: { warning } }) => warning.main,
          marginRight: "8px"
        }}
      >
        pending
      </Icon>
      <MDTypography variant="body2" color="textSecondary">
        <strong>{pendingCount} 条待审核</strong> | 共 {total} 条申请
      </MDTypography>
    </div>
  </div>
</div>

{/* 外部搜索和状态筛选区域（无MDBox版） */}
<div style={{
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  padding: "16px 24px",
  marginBottom: "24px",
  backgroundColor: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  flexWrap: "wrap", // 移动端换行
  gap: "16px" // 统一元素间距
}}>
  {/* 状态筛选 */}
  <div style={{ display: "flex", alignItems: "center" }}>
    <MDTypography variant="body2" color="textSecondary" style={{ marginRight: "12px" }}>
      状态:
    </MDTypography>
    <Select
      placeholder="全部状态"
      style={{ width: 160 }}
      value={selectedStatus}
      onChange={setSelectedStatus}
      allowClear
      size="small"
    >
      <Option value="all">全部状态</Option>
      <Option value="PENDING">待审核</Option>
      <Option value="APPROVED">已通过</Option>
      <Option value="REJECTED">已拒绝</Option>
    </Select>
  </div>

  {/* 关键词搜索 */}
  <div style={{
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    maxWidth: 400
  }}>
    <MDTypography variant="body2" color="textSecondary" style={{ marginRight: "12px", whiteSpace: "nowrap" }}>
      搜索:
    </MDTypography>
    <Input
      placeholder="申请单号/申请人/备注"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      onPressEnter={handleSearch}
      size="small"
      style={{ flexGrow: 1 }}
    />
  </div>

  {/* 操作按钮组 */}
  <div style={{ display: "flex", gap: "8px" }}>
    <MDButton
      variant="contained"
      color="info"
      size="small"
      onClick={handleSearch}
      disabled={loading}
    >
      搜索
    </MDButton>
    <MDButton
      variant="outlined"
      color="secondary"
      size="small"
      onClick={resetFilters}
      disabled={loading}
    >
      重置
    </MDButton>
  </div>
</div>
      {/* 表格区域（不含任何搜索功能） */}
      <div px={3} pb={3}>
        <DataTable
          table={{ columns, rows }}
          showTotalEntries={true}
          isSorted={false}
          noEndBorder
          entriesPerPage={{
            defaultValue: pageSize,
            entries: [5, 8, 10, 15],
          }}
          loading={loading}
          total={total}
          currentPage={currentPage}
          changePage={handlePageChange}
          canSearch={false} // 彻底关闭内部搜索功能
        />
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
